import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import FieldLabel from '../components/FieldLabel';
import { blockInvalidNumberKeys, sanitizeNumberInput } from '../utils/formHelpers';
import { outlinedFieldProps, disabledOutlinedSx } from '../utils/fieldStyles';
import { computeHssAmount } from 'utils/hss';

const isBlank = (v) => v === '' || v === null || v === undefined;

const HssSelectField = ({ field, data, change, basePrice, hssTemplates = [], onNewHssTemplate }) => {
    const key = (suffix) => `${field.key}_${suffix}`;

    const storedTemplateId = data[key('template')] || null;
    const storedPct = data[key('percentage')];
    const storedFlat = data[key('flat_charge')];

    const templateById = (id) => (hssTemplates || []).find((t) => t.id === id) || null;
    const template = templateById(storedTemplateId);
    const templatePct = template ? template.hss_percentage : '';

    // Explicit percentage wins; otherwise fall back to the template's (covers rows saved before this fix).
    const effectivePct = isBlank(storedPct) ? templatePct : storedPct;
    const isOverride =
        Boolean(template) && !isBlank(storedPct) && parseFloat(storedPct) !== parseFloat(templatePct);

    const calculatedTotal = computeHssAmount(basePrice, effectivePct, storedFlat);

    // Single write path: template, percentage, flat charge and total always move together.
    const commit = ({ templateId = storedTemplateId, percentage = storedPct, flat = storedFlat }) => {
        const tpl = templateById(templateId);
        const pctInForce = isBlank(percentage) ? (tpl?.hss_percentage ?? '') : percentage;
        change({
            [key('template')]: templateId,
            [key('percentage')]: percentage,
            [key('flat_charge')]: flat,
            [key('total')]: computeHssAmount(basePrice, pctInForce, flat) ?? '',
        });
    };

    const handleTemplateChange = (value) => {
        if (value === 'custom') {
            commit({ templateId: null });            // null, never the string 'custom'
            return;
        }
        const tpl = templateById(value);
        // Prefill from the template, but keep the flat charge and leave the % editable.
        commit({ templateId: value, percentage: tpl ? String(tpl.hss_percentage) : '' });
    };

    let percentageHelper;
    if (isOverride) percentageHelper = `Overrides template (${templatePct}%)`;
    else if (template) percentageHelper = isBlank(storedPct) ? `Using template's ${templatePct}%` : 'From template, edit to override';

    return (
        <Box sx={{ pt: 1 }}>
            <MDTypography variant="subtitle2" fontWeight="medium" mb={2} display="flex" alignItems="center">
                {field.label}
                {field.tooltip && (
                    <Tooltip title={field.tooltip} arrow placement="top">
                        <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                )}
            </MDTypography>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <TextField
                    {...outlinedFieldProps}
                    select
                    label={<FieldLabel label="HSS Template" tooltip={field.tooltips?.template} />}
                    value={storedTemplateId ?? 'custom'}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                >
                    <MenuItem value="custom">Custom / None</MenuItem>
                    {(hssTemplates || []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.template_name}</MenuItem>
                    ))}
                </TextField>
                {typeof onNewHssTemplate === 'function' && (
                    <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => onNewHssTemplate((created) => {
                            if (!created?.id) return;
                            commit({ templateId: created.id, percentage: String(created.hss_percentage ?? '') });
                        })}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 40 }}
                    >
                        Add
                    </MDButton>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="HSS Percentage (%)" tooltip={field.tooltips?.percentage} />}
                        type="number"
                        value={isBlank(storedPct) ? '' : storedPct}
                        placeholder={template ? String(templatePct) : undefined}
                        InputLabelProps={{ shrink: true }}
                        helperText={percentageHelper}
                        onKeyDown={blockInvalidNumberKeys}
                        onChange={(e) => commit({ percentage: sanitizeNumberInput(e.target.value) })}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="Additional Charge (Flat)" tooltip={field.tooltips?.flatCharge} />}
                        type="number"
                        value={isBlank(storedFlat) ? '' : storedFlat}
                        onKeyDown={blockInvalidNumberKeys}
                        onChange={(e) => commit({ flat: sanitizeNumberInput(e.target.value) })}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        label={<FieldLabel label="HSS Amount (per traveler)" tooltip={field.tooltips?.amount} />}
                        value={calculatedTotal == null ? '' : calculatedTotal.toFixed(2)}
                        disabled
                        sx={disabledOutlinedSx}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default HssSelectField;